/** Password reset, from the browser's side - card C-B, gate 1.
 *
 * Two calls, both **unauthenticated**: the person has forgotten the one thing that would give them
 * a session. Neither is a query. They are `useMutation`s with `gcTime: 0`, for the reason
 * `claims.ts` gives: a mutation's variables stay in React Query's cache for five minutes by
 * default, and these variables are **a bearer token and a new password**.
 *
 * **The request cannot be refused for an address, and this module does not pretend it can.** The
 * API answers 202 with identical bytes whether or not the address belongs to an account, so the
 * only outcomes are "accepted" and "the request did not complete". There is deliberately no field
 * of the response body carried out of here - the screen has its own sentence - so a reason added
 * to the body tomorrow is not rendered today.
 *
 * **Completion has three answers and they are three facts**, as `claims.ts` splits its own: the
 * link is no good (one answer for unknown, used, replaced and expired - an oracle otherwise), the
 * password is too weak (a different remedy: nothing was spent, type a longer one), and the request
 * did not complete (thrown).
 */

import { useMutation } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";

export type CompleteResult =
  | { status: "changed" }
  /** The link cannot be used. Says nothing about why. */
  | { status: "refused" }
  /** Too short for the server's rule. **The link was not spent.** */
  | { status: "too_weak" };

export function useRequestPasswordReset() {
  return useMutation({
    gcTime: 0,
    mutationFn: async (email: string): Promise<{ status: "accepted" }> => {
      const { error, response } = await api.POST("/api/v1/auth/password-reset/request", {
        body: { email },
      });
      if (response.ok) return { status: "accepted" };
      throw refusalFrom(response, error, "The request could not be sent.");
    },
  });
}

export function useCompletePasswordReset() {
  return useMutation({
    gcTime: 0,
    mutationFn: async (input: { token: string; newPassword: string }): Promise<CompleteResult> => {
      const { error, response } = await api.POST("/api/v1/auth/password-reset/complete", {
        body: { token: input.token, new_password: input.newPassword },
      });
      if (response.ok) return { status: "changed" };
      const failure = refusalFrom(response, error, "The password could not be changed.");
      // A 404 *carrying its own code*: a bare 404 is also what a mis-set API origin or a proxy
      // answers, and blaming the person's link for a deployment fault is the wrong remedy.
      if (response.status === 404 && failure.code === "reset_refused") return { status: "refused" };
      if (response.status === 422 && failure.code === "password_too_weak") {
        return { status: "too_weak" };
      }
      throw failure;
    },
  });
}
